import { EditOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Input, Upload } from 'antd';
import * as eva from 'eva-icons';
import moment from 'moment';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import React, { useEffect, useRef } from 'react';
import { CommentBlackIcon } from '../../../../../../assets/svg';
import {
  ACCESS_TYPE,
  DEFAULTDATEFORMAT,
  FALLBACK_PDF_URL,
  FIELD_TYPES,
  TIME_12_HOUR_FORMAT,
} from '../../../../../../common/constants';
import {
  formatNumericInput,
  formValidatorRules,
} from '../../../../../../common/utils';
import CommonSelect from '../../../../../../components/CommonSelect';
import EqcDropdown from '../../../../../../components/EqcDropdown';
import HasAccess from '../../../../../../components/HasAccess';
import CustomNomenclatureDropdown from './CustomNomenclatureDropdown';
import FieldIcon from './FieldIcon';

const MyDatePicker = DatePicker.generatePicker(momentGenerateConfig);
const MyTimePicker = MyDatePicker;

const UploadComponent = React.memo(
  ({
    uploadFieldsName,
    record,
    dataIndex,
    fileListRef,
    fieldDisabled,
    handleUploadChange,
    onUploadRemove,
    openFileDialogOnClick,
    uploadKey,
    beforeUpload,
    attachmentRef,
    onFieldClick,
    fieldIndex,
  }) => {
    if (!fileListRef?.current?.[uploadFieldsName]) {
      const initialFiles = Array.isArray(record?.[dataIndex])
        ? record?.[dataIndex]?.slice(-3)?.map((item, idx) => {
            const isPdf =
              item?.key?.split('.')?.[item?.key?.split('.')?.length - 1] ===
                'pdf' || item?.type === 'application/pdf';
            return {
              uid: `${uploadFieldsName}-${idx}`,
              status: 'done',
              url: item?.value || item?.url,
              key: item?.key,
              name:
                item?.name ||
                (item?.key ? item?.key?.split('/')?.pop() : undefined),
              blobUrl: item?.blobUrl,
              thumbUrl: isPdf ? FALLBACK_PDF_URL : item?.value || item?.url,
            };
          })
        : [];
      // eslint-disable-next-line no-param-reassign
      fileListRef.current[uploadFieldsName] = initialFiles;
    } else {
      // eslint-disable-next-line no-param-reassign
      fileListRef.current[uploadFieldsName] =
        fileListRef?.current[uploadFieldsName]?.slice(-3);
    }

    const handlePreview = async (file) => {
      const previewUrl =
        file?.blobUrl ||
        (file?.originFileObj && URL?.createObjectURL(file?.originFileObj)) ||
        (file?.url !== FALLBACK_PDF_URL ? file?.url : null) ||
        (file?.thumbUrl !== FALLBACK_PDF_URL ? file?.thumbUrl : null);

      if (previewUrl) {
        // eslint-disable-next-line no-undef
        window?.open(previewUrl, '_blank');

        if (file?.originFileObj) {
          URL?.revokeObjectURL(previewUrl);
        }
      }
    };

    return (
      <Upload
        disabled={fieldDisabled}
        openFileDialogOnClick={openFileDialogOnClick}
        key={uploadKey}
        maxCount={3}
        accept=".png,.jpg,.jpeg,.pdf"
        multiple
        listType="picture-card"
        className="register-attachment"
        fileList={fileListRef?.current?.[uploadFieldsName]?.slice(-3)}
        showUploadList={{
          showPreviewIcon: true,
          showRemoveIcon: true,
          showDownloadIcon: false,
        }}
        beforeUpload={(file) => beforeUpload(file)}
        onPreview={handlePreview}
        onChange={(info) => handleUploadChange(info)}
        onRemove={onUploadRemove}
        id={`register-field-upload-${fieldIndex}`}
      >
        {(!fileListRef?.current[uploadFieldsName] ||
          fileListRef?.current[uploadFieldsName]?.length < 3) && (
          <div className="height-percent-100 width-percent-100 d-flex align-center justify-center pointer">
            <Button
              tabIndex={0}
              className="upload-attachment-button"
              type="primary"
              shape="round"
              ref={attachmentRef}
              onClick={onFieldClick}
              disabled={fieldDisabled}
              id={`register-upload-btn-${fieldIndex}`}
            >
              Upload
            </Button>
          </div>
        )}
      </Upload>
    );
  },
);

const RenderFields = ({
  fieldType,
  fieldIndex,
  fieldName,
  initialFieldValue,
  fieldIsRequired,
  formClassName = '',
  fieldDisabled = false,
  divClassName = '',
  onFieldClick = () => {},
  onTextChange = () => {},
  onTextBlur = () => {},
  onNumericChange = () => {},
  onNumericBlur = () => {},
  onOptionChange = () => {},
  optionsOpen = {},
  onOptionsFocus = () => {},
  options,
  onOptionOpenChange = () => {},
  onOptionBlur = () => {},
  popupClassName = {},
  multiSelectOpen = {},
  onMultiSelectFocus = () => {},
  onMultiSelectBlur = () => {},
  onMultiSelectOpenChange = () => {},
  onMultiSelectChange = () => {},
  getPopupContainer = () => {},
  dateOpen = {},
  onDateFocused = {},
  onDateOpenChange = () => {},
  onDateChange = () => {},
  onDateBlur = () => {},
  datePopupClassName = '',
  timeOpen = {},
  onTimeFocused = () => {},
  onTimeOpenChange = () => {},
  onTimeChange = () => {},
  onTimeBlur = () => {},
  timeRef = null,
  onCalculationChange = () => {},
  onCalculationBlur = () => {},
  calculateFieldValue = () => {},
  openFileDialogOnClick = false,
  fileListRef = {},
  records = {},
  uploadDataIndex = '',
  uploadFieldName = '',
  handleUploadChange = () => {},
  inputRef = null,
  multiLineRef = null,
  numericRef = null,
  optionsRef = null,
  multiSelectRef = null,
  dateRef = null,
  attachmentRef = null,
  uploadKey = '',
  beforeUpload = () => {},
  onUploadRemove = () => {},
  showCommentIcon = false,
  hasComments = false,
  onCommentClick = () => {},
  onNomenclatureChange = () => {},
  defaultOptions = [],
  onEqcChange = () => {},
  onEqcBlur = () => {},
  nomenclatureStatus = false,
  popupContainer,
}) => {
  const { characterWithoutWhiteSpace } = formValidatorRules;
  const eqcDropdownRef = useRef(null);

  const hasInspectionAccess = HasAccess({ type: ACCESS_TYPE?.INSPECTION });

  useEffect(() => {
    eva.replace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderLabelWithComment = () => {
    if (!fieldName) return null;
    return (
      <span className="d-flex align-center white-space-pre-line text-break">
        <div className="mr-5">
          <FieldIcon fieldType={fieldType} />
        </div>
        {fieldName}
        {showCommentIcon && (
          <CommentBlackIcon
            onClick={(e) => {
              e?.preventDefault();
              onCommentClick();
            }}
            className={`ml-10 pointer ${hasComments ? 'has-remark' : 'no-remark'}`}
          />
        )}
      </span>
    );
  };

  switch (fieldType) {
    case FIELD_TYPES.TEXT:
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={initialFieldValue}
          label={renderLabelWithComment()}
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
            characterWithoutWhiteSpace,
            {
              max: 250,
              message: 'Text cannot be more than 250 characters',
            },
          ]}
        >
          <Input
            placeholder="Enter text"
            ref={inputRef}
            disabled={fieldDisabled}
            onChange={onTextChange}
            onClick={onFieldClick}
            onBlur={onTextBlur}
            id={`register-field-text-${fieldIndex}`}
          />
        </Form.Item>
      );

    case FIELD_TYPES.MULTI_LINE_TEXT:
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={initialFieldValue}
          label={renderLabelWithComment()}
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
            {
              max: 350,
              message: 'Maximum 350 characters are allowed',
            },
          ]}
        >
          <Input.TextArea
            placeholder="Enter text"
            rows={4}
            ref={multiLineRef}
            disabled={fieldDisabled}
            onChange={onTextChange}
            onClick={onFieldClick}
            onBlur={onTextBlur}
            id={`register-field-multiline-${fieldIndex}`}
          />
        </Form.Item>
      );

    case FIELD_TYPES.NUMERIC:
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={initialFieldValue}
          label={renderLabelWithComment()}
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
            {
              max: 15,
              message: 'Maximum 15 digits are allowed',
            },
            {
              validator: (_, value) => {
                if (!value || value === '' || value === '-') {
                  return Promise?.resolve();
                }
                const numericValue = Number(value);
                if (Number?.isNaN(numericValue)) {
                  return Promise?.reject(
                    new Error('Please enter a valid number'),
                  );
                }
                return Promise?.resolve();
              },
            },
          ]}
          normalize={(value) => formatNumericInput(value)}
        >
          <Input
            placeholder="Enter number"
            inputMode="decimal"
            disabled={fieldDisabled}
            ref={numericRef}
            onChange={onNumericChange}
            onBlur={onNumericBlur}
            onClick={onFieldClick}
            id={`register-field-numeric-${fieldIndex}`}
          />
        </Form.Item>
      );

    case FIELD_TYPES.OPTIONS:
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={initialFieldValue || undefined}
          label={renderLabelWithComment()}
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
          ]}
        >
          <CommonSelect
            placeholder="Select Option"
            tabIndex={0}
            disabled={fieldDisabled}
            name={fieldName}
            open={optionsOpen}
            inputRef={optionsRef}
            onFocus={onOptionsFocus}
            onChange={onOptionChange}
            options={options}
            onOpenChange={onOptionOpenChange}
            onBlur={onOptionBlur}
            allowClear
            showSearch
            popupClassName={popupClassName}
            onClick={onFieldClick}
            getPopupContainer={getPopupContainer}
            id={`register-field-options-${fieldIndex}`}
          />
        </Form.Item>
      );

    case FIELD_TYPES.CHECKBOXES:
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={initialFieldValue}
          label={renderLabelWithComment()}
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
          ]}
        >
          <CommonSelect
            mode="multiple"
            placeholder="Select Options"
            tabIndex={0}
            disabled={fieldDisabled}
            name={fieldName}
            open={multiSelectOpen}
            inputRef={multiSelectRef}
            onFocus={onMultiSelectFocus}
            onBlur={onMultiSelectBlur}
            onOpenChange={onMultiSelectOpenChange}
            onChange={onMultiSelectChange}
            options={options}
            allowClear
            showSearch
            popupClassName={popupClassName}
            onClick={onFieldClick}
            getPopupContainer={getPopupContainer}
            id={`register-field-checkboxes-${fieldIndex}`}
          />
        </Form.Item>
      );

    case FIELD_TYPES.DATE:
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={
            initialFieldValue && moment(initialFieldValue, DEFAULTDATEFORMAT)
          }
          label={renderLabelWithComment()}
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
          ]}
        >
          <MyDatePicker
            open={dateOpen}
            onFocus={onDateFocused}
            onOpenChange={onDateOpenChange}
            onChange={onDateChange}
            // ! Commented for future use
            // onChange={(value) => {
            //   if (onDateChange) {
            //     onDateChange(value);
            //   }
            // }}
            ref={dateRef}
            className="width-percent-100 register-date-picker"
            disabled={fieldDisabled}
            readOnly
            onClick={onFieldClick}
            suffixIcon={<i data-eva="calendar-outline" />}
            format={DEFAULTDATEFORMAT}
            classNames={{ popup: { root: datePopupClassName } }}
            onBlur={onDateBlur}
            id={`register-field-date-${fieldIndex}`}
          />
        </Form.Item>
      );

    case FIELD_TYPES.TIME:
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={initialFieldValue}
          label={renderLabelWithComment()}
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
          ]}
        >
          <MyTimePicker
            picker="time"
            open={timeOpen}
            onFocus={onTimeFocused}
            onOpenChange={onTimeOpenChange}
            onChange={onTimeChange}
            ref={timeRef}
            className="width-percent-100 register-time-picker width-200"
            disabled={fieldDisabled}
            readOnly
            onClick={onFieldClick}
            format={TIME_12_HOUR_FORMAT}
            use12Hours
            onBlur={onTimeBlur}
          />
        </Form.Item>
      );

    case FIELD_TYPES.ATTACHMENT:
      return (
        <div className={divClassName}>
          <Form.Item
            name={fieldIndex}
            label={renderLabelWithComment()}
            initialValue={initialFieldValue}
            rules={[
              {
                required: fieldIsRequired,
                message: 'This field is required!',
              },
            ]}
          >
            <UploadComponent
              uploadFieldsName={uploadFieldName}
              record={records}
              dataIndex={uploadDataIndex}
              fileListRef={fileListRef}
              fieldDisabled={fieldDisabled}
              handleUploadChange={handleUploadChange}
              onUploadRemove={onUploadRemove}
              openFileDialogOnClick={openFileDialogOnClick}
              uploadKey={uploadKey}
              beforeUpload={beforeUpload}
              attachmentRef={attachmentRef}
              onFieldClick={onFieldClick}
              fieldIndex={fieldIndex}
            />
          </Form.Item>
        </div>
      );

    case FIELD_TYPES.CALCULATION: {
      const calculatedValue = calculateFieldValue();
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={calculatedValue || initialFieldValue || '-'}
          label={renderLabelWithComment()}
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
          ]}
        >
          <Input
            disabled
            placeholder="-"
            onChange={(e) => onCalculationChange(e)}
            onBlur={onCalculationBlur}
            id={`register-field-calculation-${fieldIndex}`}
          />
        </Form.Item>
      );
    }
    case FIELD_TYPES?.NOMENCLATURE:
      if (!nomenclatureStatus) {
        return (
          <Form.Item
            name={fieldIndex}
            className={formClassName}
            initialValue={initialFieldValue}
            label={renderLabelWithComment()}
            rules={[
              {
                required: fieldIsRequired,
                message: 'This field is required!',
              },
              characterWithoutWhiteSpace,
              {
                max: 250,
                message: 'Text cannot be more than 250 characters',
              },
            ]}
          >
            <Input
              placeholder="Enter text"
              ref={inputRef}
              disabled={fieldDisabled}
              onChange={onTextChange}
              onClick={onFieldClick}
              onBlur={onTextBlur}
              id={`register-field-nomenclature-text-${fieldIndex}`}
            />
          </Form.Item>
        );
      }
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={initialFieldValue}
          label={renderLabelWithComment()}
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
          ]}
        >
          <CustomNomenclatureDropdown
            onChange={onNomenclatureChange}
            isFieldDisabled={fieldDisabled}
            nomenclatureStatus={nomenclatureStatus}
            popupContainer={popupContainer}
          />
        </Form.Item>
      );

    case FIELD_TYPES?.EQC:
      return (
        <Form.Item
          name={fieldIndex}
          className={formClassName}
          initialValue={initialFieldValue}
          label={
            fieldName && (
              <span className="d-flex align-center width-percent-100 gap-8 white-space-pre-line text-break">
                <div className="d-flex align-center">
                  <div className="mr-5">
                    <FieldIcon fieldType={fieldType} />
                  </div>
                  {fieldName}
                  {showCommentIcon && (
                    <CommentBlackIcon
                      onClick={(e) => {
                        e?.preventDefault();
                        onCommentClick();
                      }}
                      className={`ml-10 pointer ${hasComments ? 'has-remark' : 'no-remark'}`}
                    />
                  )}
                </div>
                {!(fieldDisabled || !hasInspectionAccess) && (
                  <EditOutlined
                    onClick={() => {
                      eqcDropdownRef?.current?.openEditMode();
                    }}
                  />
                )}
              </span>
            )
          }
          rules={[
            {
              required: fieldIsRequired,
              message: 'This field is required!',
            },
          ]}
        >
          <EqcDropdown
            ref={eqcDropdownRef}
            disabled={fieldDisabled || !hasInspectionAccess}
            hasAccess={hasInspectionAccess}
            onChange={onEqcChange}
            onBlur={onEqcBlur}
            defaultOptions={defaultOptions}
          />
        </Form.Item>
      );
    default:
  }
};

export default React.memo(RenderFields);
